import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { EntityService, myDesignService } from "../../../services";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DataGrid } from "@mui/x-data-grid";
import {
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Box,
  Paper,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Typography } from "@mui/material";
import CampaignContentManager from "./CampaignContentManager";
import ResponsiveDialogManager from "../../../components/dialog/ResponsiveDialogManager";
import AddIcon from "@mui/icons-material/Add";
import CreateCampaignForm from "../../../components/forms/CreateCampaignForm";
import { useViewport } from "../../../helpers";
import CreateIcon from "@mui/icons-material/Create";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { cleanCampaigns } from "../../../reducers/myDesigns/myDesignsSlice";
import {
  fetchCampaigns,
  fetchScheduledCampaigns,
} from "../../../reducers/myDesigns/myDesignsThunks";

const PREFIX = "MyDesignCampaigns";

const classes = {
  rootPaper: `${PREFIX}-rootPaper`,
  dataGridSize: `${PREFIX}-dataGridSize`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.rootPaper}`]: {
    marginBottom: "1rem",
    backgroundColor: "#484848",
  },

  [`& .${classes.dataGridSize}`]: {
    height: 520,
  },
}));

function TabPanel(props) {
  const { children, value, index, disableGutters, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      key={index + 1}
      {...other}
    >
      {value === index && <Box p={disableGutters ? 0 : 3}>{children}</Box>}
    </div>
  );
}

const MyDesignCampaigns = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();
  const [entities, setEntities] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [createCampaign, setCreateCampaign] = useState(false);
  const [campaignTab, setCampaignTab] = useState(0);
  const scheduledCampaigns = useSelector(
    (state) => state.myDesigns.currentDesign?.scheduledCampaigns
  );
  const campaignList = useSelector(
    (state) => state.myDesigns.currentDesign?.campaignList
  );
  const container = useRef();

  const columns = [
    { field: "id", headerName: "Id", width: 70, type: "number" },
    {
      field: "name",
      headerName: t("myDesign.campaigns.nameColumn"),
      width: 120,
    },
    { field: "type", headerName: "Type", width: 90 },
    {
      field: "action",
      headerName: "Actions",
      renderCell: (params) => (
        <div>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setSelectedCampaign(params.row)}
          >
            <CreateIcon />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleVisibleChange(params.row)}
          >
            {params.row.visible ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      ),
    },
    {
      field: "published",
      headerName: t("myDesign.campaigns.publishedColumn"),
      width: 120,
      renderCell: (params) => (
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={params.row.published === 1 ? true : false}
                onChange={() => handlePublishedChange(params.row)}
                name="checkedB"
                color="primary"
              />
            }
          />
        </div>
      ),
    },
  ];

  const columnsContent = [
    { field: "id", headerName: "Id", width: 70, type: "number" },
    {
      field: "name",
      headerName: t("myDesign.campaigns.nameColumn"),
      width: 120,
    },
    { field: "type", headerName: "Type", width: 90 },
    {
      field: "algorithm",
      headerName: t("myDesign.campaigns.algorithmColumn"),
      width: 100,
    },
    {
      field: "action",
      headerName: "Actions",
      renderCell: (params) => {
        if (params.row.algorithm === "PLAYLIST") {
          return (
            <div>
              <IconButton
                variant="contained"
                color="primary"
                size="small"
                onClick={() => setSelectedCampaign(params.row)}
              >
                <PlaylistPlayIcon />
              </IconButton>
            </div>
          );
        } else {
          return null;
        }
      },
    },
  ];

  function a11yProps(index) {
    return {
      id: `scrollable-prevent-tab-campaign-${index}`,
      "aria-controls": `scrollable-prevent-tabpanel-${index}`,
    };
  }

  const myFetchCampaigns = async () => {
    try {
      const thunk = await dispatch(
        fetchCampaigns({
          frameId: props.frameId,
          targetId: props.selectedTarget.id,
        })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
    } catch (err) {
      enqueueSnackbar(t("toasts.fetchCampaignsError"), { variant: "error" });
    }
  };

  const myFetchScheduledCampaigns = async (frameId, targetId) => {
    try {
      const thunk = await dispatch(
        fetchScheduledCampaigns({ frameId, targetId })
      );
      if (thunk.meta.requestStatus === "rejected")
        throw new Error("recovery error");
    } catch (err) {
      enqueueSnackbar(t("toasts.fetchCampaignsError"), { variant: "error" });
    }
  };

  const handleVisibleChange = (item) => {
    myDesignService
      .updateCampaignVisible(item.id, item.visible === 1 ? false : true)
      .then(() => {
        enqueueSnackbar(t("toasts.updateCampaignSuccess"), {
          variant: "success",
        });
        myFetchScheduledCampaigns(props.frameId, props.selectedTarget.id);
        myFetchCampaigns();
      });
  };

  const handlePublishedChange = (item) => {
    myDesignService
      .updateCampaignPublish(item.id, item.published === 1 ? false : true)
      .then(() => {
        enqueueSnackbar(t("toasts.updateCampaignSuccess"), {
          variant: "success",
        });
        myFetchScheduledCampaigns(props.frameId, props.selectedTarget.id);
        myFetchCampaigns();
      });
  };

  useEffect(() => {
    if (props.selectedTarget?.id) {
      myFetchScheduledCampaigns(props.frameId, props.selectedTarget.id);
      myFetchCampaigns();
      EntityService.getEntities("sphere").then((result) => {
        setEntities(result.data);
      });

      const containerNode = container.current;
      if (!containerNode) {
        return;
      }
      containerNode.addEventListener("touchstart", isolateTouch, {
        passive: true,
      });
      containerNode.addEventListener("touchmove", isolateTouch, {
        passive: true,
      });
      containerNode.addEventListener("touchend", isolateTouch, {
        passive: true,
      });

      return () => {
        if (!containerNode) {
          return;
        }
        containerNode.removeEventListener("touchstart", isolateTouch, {
          passive: true,
        });
        containerNode.removeEventListener("touchmove", isolateTouch, {
          passive: true,
        });
        containerNode.removeEventListener("touchend", isolateTouch, {
          passive: true,
        });
        dispatch(cleanCampaigns());
      };
    }
  }, [props.selectedTarget?.id]);

  function isolateTouch(e) {
    e.stopPropagation();
  }

  return (
    <div style={{ height: "600px", margin: "0 auto" }} ref={container}>
      <Root>
        {scheduledCampaigns && campaignList && entities ? (
          <React.Fragment>
            <ResponsiveDialogManager
              open={selectedCampaign && campaignTab === 1 ? true : false}
              disableGutters
              fullScreen
              renderer={
                <CampaignContentManager selectedCampaign={selectedCampaign} />
              }
              title={t("myDesign.campaigns.campaignContentManagement")}
              onClose={() => {
                setSelectedCampaign(null);
              }}
            />
            <ResponsiveDialogManager
              open={
                createCampaign || (campaignTab === 0 && selectedCampaign)
                  ? true
                  : false
              }
              disableGutters
              fullScreen
              renderer={
                <CreateCampaignForm
                  selectedCampaign={selectedCampaign}
                  selectedTarget={props.selectedTarget}
                  entities={entities}
                  setCreateCampaign={() => {
                    setCreateCampaign(false);
                    setSelectedCampaign(null);
                  }}
                />
              }
              title={
                selectedCampaign
                  ? `${t("myDesign.campaigns.updateCampaignButton")}: ${
                      props.selectedTarget.label
                    }`
                  : `${t("myDesign.campaigns.createCampaignButton")}: ${
                      props.selectedTarget.label
                    }`
              }
              onClose={() => {
                setCreateCampaign(false);
                setSelectedCampaign(null);
              }}
            />
            <Paper className={classes.rootPaper}>
              <Tabs
                value={campaignTab}
                onChange={(e, value) => setCampaignTab(value)}
                indicatorColor="primary"
                textColor="primary"
                centered
              >
                <Tab
                  label={t("myDesign.campaigns.campaignList")}
                  wrapped={width < 1024}
                  {...a11yProps(0)}
                />
                <Tab
                  label={t("myDesign.campaigns.scheduledCampaigns")}
                  wrapped={width < 1024}
                  {...a11yProps(1)}
                />
              </Tabs>
            </Paper>
            <TabPanel value={campaignTab} index={0} disableGutters>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setCreateCampaign(true)}
                style={{
                  marginBottom: "1rem",
                  position: "relative",
                  left: "50%",
                  transform: "translateX(-50%)",
                }}
              >
                <AddIcon style={{ marginRight: "0.5rem" }} />
                {t("myDesign.campaigns.createCampaignButton")}
              </Button>
              {campaignList.length === 0 ? (
                <Typography variant="body1" style={{ textAlign: "center" }}>
                  {t("myDesign.campaigns.noCampaigns")}
                </Typography>
              ) : width < 1024 ? (
                <div style={{ height: 450 }}>
                  <DataGrid
                    className={classes.dataGridSize}
                    disableRowSelectionOnClick
                    rows={campaignList}
                    columns={columns}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    pageSizeOptions={[5, 10, 25, 50]}
                  />
                </div>
              ) : (
                <DataGrid
                  className={classes.dataGridSize}
                  disableRowSelectionOnClick
                  rows={campaignList}
                  columns={columns}
                  autoHeight
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              )}
            </TabPanel>
            <TabPanel value={campaignTab} index={1} disableGutters>
              {scheduledCampaigns.length === 0 ? (
                <Typography variant="body1" style={{ textAlign: "center" }}>
                  {t("myDesign.campaigns.noCampaigns")}
                </Typography>
              ) : (
                <DataGrid
                  className={classes.dataGridSize}
                  disableRowSelectionOnClick
                  rows={scheduledCampaigns}
                  columns={columnsContent}
                  autoHeight
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              )}
            </TabPanel>
          </React.Fragment>
        ) : (
          <div className="loading-container">
            <CircularProgress color={"primary"} />
            <Typography>{t("loaders.fetching")}</Typography>
          </div>
        )}
      </Root>
    </div>
  );
};

export default MyDesignCampaigns;
